import { HttpErrorResponse } from '@angular/common/http';
import { AfterContentChecked, ChangeDetectorRef, Component, inject, input, viewChild, output } from '@angular/core';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Subject } from 'rxjs';

import { SummaryButton, SummaryItem } from '../../../models';
import { MockEditService } from '../../../mock';
import { BaseEditService, BaseEditCreateService, NotificationService } from '../../../services';
import { BaseEditFormComponent } from '../../base-edit-form/base-edit-form.component';
import { SummaryTopComponent } from '../../summary-top/summary-top.component';

@Component({
  selector: 'app-base-edit-form-modal',
  templateUrl: './base-edit-form-modal.component.html',
  styleUrl: './base-edit-form-modal.component.scss',
  imports: [SummaryTopComponent, BaseEditFormComponent],
})
export class BaseEditFormModalComponent implements AfterContentChecked {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly notificationService = inject(NotificationService);

  public readonly form = viewChild(BaseEditFormComponent);

  public readonly id = input<string>();
  public readonly parentId = input<string>();
  public readonly parentId2 = input<string>();
  public readonly additionalButtons = input<SummaryButton[]>();
  public readonly alwaysShowAdditionalButtons = input(false);
  public readonly alwaysSave = input(false);
  public readonly closeOnSave = input(true);
  public readonly fields = input<FormlyFieldConfig[]>();
  public readonly header = input<string>();
  public readonly showSave = input(true);
  public readonly showBack = input(true);
  public readonly saveButtonText = input($localize`Save`);
  public readonly summaryItems = input<(model: any) => SummaryItem[]>();
  public readonly baseService = input<BaseEditService<any, any> | BaseEditCreateService<any, any>>(
    new MockEditService(),
  );
  public readonly model = input<any>({});
  // Opt-in: a refused save shows the API's own message (a plain-string 400 body) instead of the
  // generic "... save failed". Off by default, so every other modal keeps its toast exactly as it
  // was. The re-measure modal turns it on, where the refusal says what the roll owes (Ross Cut
  // Stock, AC-19a).
  public readonly showSaveRefusal = input(false);
  public readonly modelUpdated = output<any>();
  public readonly saved = output<any>();
  // Re-exposes the edit form's saveFailed, the same opt-in: when a host listens, the generic toast
  // is not shown and the host owns the messaging - e.g. a coded refusal it answers with a Yes/No
  // and re-posts (the make-PO picking list guard, 2026-10-05). Unobserved, nothing changes.
  private readonly saveFailed$ = new Subject<HttpErrorResponse>();
  public readonly saveFailed = outputFromObservable(this.saveFailed$);

  ngAfterContentChecked() {
    this.cdRef.detectChanges();
  }

  // Bound to the form's (saveFailed), which the form only emits when someone listens - so the
  // generic toast the form would otherwise have shown is shown here when the modal has not opted in.
  protected onSaveFailed(error: HttpErrorResponse) {
    if (this.saveFailed$.observed) {
      this.saveFailed$.next(error);
      return;
    }

    const name = this.baseService()?.name;
    if (this.showSaveRefusal() && typeof error?.error === 'string' && error.error.trim()) {
      this.notificationService.apiFail(error, name);
      return;
    }

    this.notificationService.saveFail(name);
  }

  updateModel(model: any, markUpdated = true) {
    this.form().updateModel(model, markUpdated);
  }
}
